import { useContext, useEffect, useState } from 'react';

import {
  control,
  ControlPosition,
  data,
  HtmlMarker,
  layer,
  source
} from 'azure-maps-control';
import { AzureMapsContext, IAzureMapsContextProps } from 'react-azure-maps';
import { boundingBoxNetherlands } from './constants';

import {
  BLUE,
  BLUE_DARK,
  BLUE_LIGHT,
  DARK,
  GRAY,
  GRAY_SECONDARY,
  MINT,
  ORANGE,
  ORANGE_SECONDARY,
  PRIMARY,
  RED,
  SECONDARY,
  TURQUOISE,
  YELLOW
} from 'constants/colors';

import { Box, styled } from '@mui/material';

import MapComponent from 'components/shared/Map/MapComponent';
import { LatitudeLongitudePair } from 'components/roster-portal/activity-planning/types';

const StyledMapComponent = styled(Box)(() => ({
  width: '100%',
  height: '100%',
  '.pin, .pin-to-plan': {
    width: '30px',
    height: '30px',
    borderRadius: '50% 50% 50% 0',
    transform: 'rotate(-45deg)',
    left: '50px',
    top: '50px',
    margin: '-20px 0 0 -20px',
    backgroundSize: 'cover',
    overflow: 'hidden'
  },
  '.pin': {
    border: '3px solid ' + PRIMARY,
    backgroundColor: PRIMARY
  },
  '.pin-to-plan': {
    border: '3px solid ' + ORANGE,
    backgroundColor: ORANGE
  },
  '.pin img, .pin-to-plan img': {
    transform: 'rotate(45deg)',
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    objectPosition: 'center'
  },
  '.pin:hover': {
    cursor: 'pointer'
  },

  '.number': {
    position: 'absolute',
    top: '45%',
    left: '0%',
    transform: 'rotate(45deg)',
    fontSize: '10px',
    color: 'rgb(0, 0, 0)',
    fontFamily: '"Heebo", sans-serif',
    textShadow:
      '-0.5px 0.5px 0 white, 0.5px 0.5px 0.5px white, 0.5px -0.5px 0 white, -0.5px -0.5px 0 white'
  }
}));

interface Props {
  pointSet: LatitudeLongitudePair[][];
}

const MapController = ({ pointSet }: Props) => {
  const { mapRef, isMapReady } =
    useContext<IAzureMapsContextProps>(AzureMapsContext);

  const routeColors = [
    PRIMARY,
    SECONDARY,
    ORANGE,
    BLUE,
    ORANGE_SECONDARY,
    BLUE_DARK,
    TURQUOISE,
    MINT,
    BLUE_LIGHT,
    DARK,
    YELLOW,
    RED,
    GRAY,
    GRAY_SECONDARY
  ];
  const [datasource, setDatasource] = useState<source.DataSource>();

  useEffect(() => {
    if (isMapReady && mapRef) {
      const zoomControl = new control.ZoomControl();

      /* Add the zoom control to the map*/
      mapRef?.controls.add(zoomControl, {
        position: ControlPosition.BottomRight
      });
      const newDatasource = new source.DataSource();
      setDatasource(newDatasource);
      mapRef?.sources.add(newDatasource);
      const lineLayerRef = new layer.LineLayer(newDatasource, 'line', {
        strokeColor: ['get', 'color'],
        strokeWidth: 5,
        lineJoin: 'round',
        lineCap: 'round'
      });
      mapRef?.layers.add(lineLayerRef);
      updateMap(newDatasource);
    }
  }, [isMapReady]);

  useEffect(() => {
    if (isMapReady && mapRef && datasource) {
      updateMap(datasource);
    }
  }, [pointSet]);

  function addMarkers(positions: number[][]) {
    if (positions.length > 0) {
      addMarker(positions[0]);
      if (positions.length > 1) {
        addMarker(positions[positions.length - 1]);
      }
    }
  }

  function addMarker(position: number[]) {
    const marker = new HtmlMarker({
      position,
      htmlContent: `<div><div class='pin'></div></div>`
    });

    mapRef?.markers.add(marker);
  }

  const updateMap = (datasource: source.DataSource) => {
    datasource.clear();
    mapRef?.markers.clear();

    const routesPositions: data.Feature<
      data.LineString,
      {
        color: string;
      }
    >[] = [];

    if (pointSet && pointSet.length > 0) {
      pointSet.forEach((points, i) => {
        const positions = points.map((p) => [p.longitude, p.latitude]);
        if (positions.length > 0) {
          const lineString = new data.LineString(positions);
          const features = [
            new data.Feature(lineString, { color: routeColors[i] })
          ];
          datasource.add(features);
          routesPositions.push(features[0]);
          addMarkers(positions);
        }
      });
      const boundingBox =
        routesPositions.length > 0
          ? data.BoundingBox.fromData(routesPositions)
          : boundingBoxNetherlands;

      mapRef?.setCamera({
        bounds: boundingBox,
        padding: 50
      });
    }
  };

  return (
    <StyledMapComponent>
      <MapComponent />
    </StyledMapComponent>
  );
};

export default MapController;
